import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { countDifferences } from './permissions-widget';

import { store } from '@/atoms';

export const TEMP_clearPermissionsStorage = () => {
  localStorage.removeItem('perms-widget.permissions.v2');
  localStorage.removeItem('perms-widget.permissions-applied-to-token.v2');
  localStorage.removeItem('perms-widget.permissions-on-actual-token.v2');
  localStorage.removeItem('perms-widget.initialized.v2');
};

/**
 * Hardcoded impersonation toggles. Exported so the UI can render every option
 * even when `atomWithStorage` still has a stale v2 object — adding a key here
 * does not merge into existing localStorage. `getPermissionsWidgetDisplayKeys`
 * unions this list with extra keys from storage, the real token, or the last
 * applied impersonation state (e.g. legacy `PATHWAY_SMART_SEARCH`) so new options
 * show up without a storage bump.
 */
export const permissionsWidgetOptions = [
  'LEADER',
  'TRAINER',
  'ADMIN',
  'LICENSEESTAFF',
  'STAKEHOLDER',
  'SEARCH_ALT_PROCEDURE',
  'SMART_SEARCH',
  'LOGIN',
  'OPERATOR',
] as const;

/** Known options first, then any other keys in draft, real token, or applied state. */
export const getPermissionsWidgetDisplayKeys = (
  stored: Record<string, boolean>,
  onActualToken: string[],
  applied: Record<string, boolean> = {},
): string[] => {
  const optionSet = new Set<string>(permissionsWidgetOptions);
  const extraKeys = [
    ...new Set([
      ...Object.keys(stored),
      ...onActualToken,
      ...Object.keys(applied),
    ]),
  ]
    .filter(key => !optionSet.has(key))
    .sort();
  return [...permissionsWidgetOptions, ...extraKeys];
};

/** Rebuild draft toggles from the real token; omit keys not on the real token. */
export const getPermissionsResetToActualToken = (
  stored: Record<string, boolean>,
  onActualToken: string[],
  applied: Record<string, boolean> = {},
): Record<string, boolean> =>
  getPermissionsWidgetDisplayKeys(stored, onActualToken, applied).reduce<
    Record<string, boolean>
  >((acc, permission) => {
    if (onActualToken.includes(permission)) {
      acc[permission] = true;
    }
    return acc;
  }, {});

/** Treat missing keys as false so stale storage matches display and Apply state. */
export const normalizePermissionsForKeys = (
  keys: string[],
  perms: Record<string, boolean>,
): Record<string, boolean> =>
  keys.reduce<Record<string, boolean>>((acc, key) => {
    acc[key] = perms[key] ?? false;
    return acc;
  }, {});

const possiblePermissions = permissionsWidgetOptions;

const permissions = possiblePermissions.reduce<Record<string, boolean>>(
  (acc, permission) => ({ ...acc, [permission]: false }),
  {},
);

export const openAtom = atom(false);
export const permissionsAtom = atomWithStorage(
  'perms-widget.permissions.v2',
  permissions,
  undefined,
  {
    getOnInit: true,
  },
);
export const permissionsAppliedToTokenAtom = atomWithStorage(
  'perms-widget.permissions-applied-to-token.v2',
  permissions,
  undefined,
  {
    getOnInit: true,
  },
);
export const permissionsOnActualTokenAtom = atomWithStorage(
  'perms-widget.permissions-on-actual-token.v2',
  [] as string[],
  undefined,
  {
    getOnInit: true,
  },
);
export const permissionsWidgetInitializedAtom = atomWithStorage(
  'perms-widget.initialized.v2',
  false,
  undefined,
  {
    getOnInit: true,
  },
);
export const tokenHasChangesAtom = atom(get => {
  const permissions = get(permissionsAtom);
  const tokenPermissions = get(permissionsOnActualTokenAtom);
  const appliedPerms = get(permissionsAppliedToTokenAtom);
  const displayKeys = getPermissionsWidgetDisplayKeys(
    permissions,
    tokenPermissions,
    appliedPerms,
  );
  const basePerms = displayKeys.reduce<Record<string, boolean>>(
    (acc, permission) => ({
      ...acc,
      [permission]: tokenPermissions.includes(permission),
    }),
    {},
  );
  const diff = countDifferences(
    basePerms,
    normalizePermissionsForKeys(displayKeys, appliedPerms),
  );
  return diff !== 0;
});

export const togglePermission = (permission: string) => {
  const permissions = { ...store.get(permissionsAtom) };
  const current = permissions[permission] ?? false;

  if (current) {
    delete permissions[permission];
  } else {
    permissions[permission] = true;
  }

  store.set(permissionsAtom, permissions);
};

export const setSomePermissions = (permissions: Record<string, boolean>) => {
  const newPermissions = { ...store.get(permissionsAtom) };
  Object.entries(permissions).forEach(([permission, toggle]) => {
    newPermissions[permission] = toggle;
  });
  store.set(permissionsAtom, newPermissions);
};
